import React, { useState, useEffect } from 'react';

import { InputNumber, Input, Form, Select, Switch, Modal, Tooltip } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';

import IBaseComponentProps from '@components/base-component-props';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { getEnumList } from '@extensions/utils';
import { exception } from '@extensions/notification';

import { IUserSession } from '@entities/user-session';
import { IWaypoint } from '@entities/waypoint';

import { Permission, hasPermission } from '@enums/permission';
import { WaypointType, enumLabel as waypointTypeLabel } from '@enums/waypoint-type';
import { RouteZone, enumLabel as routeZoneLabel } from '@enums/route-zone';

interface IWaypointProps extends IBaseComponentProps {
    headerId: string | undefined;
    id: string | undefined;
    routeZone: RouteZone | undefined;
    onSave: () => void;
    onCancel: () => void;
    api: NotificationInstance;
}

const Waypoint = (props: IWaypointProps) => {
    const { headerId, id, routeZone, api, serverFetch, onSave, onCancel } = props;

    const [form] = Form.useForm();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entity, setEntity] = useState<IWaypoint>();

    const [waypointTypes] = useState(getEnumList(WaypointType, waypointTypeLabel));
    const [routeZones] = useState(getEnumList(RouteZone, routeZoneLabel));
    const [isViewOnly] = useState<boolean>(!hasPermission(userSession.permissions, Permission.ManageWaypoints));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            const promises = [];

            if (id) {
                promises.push(
                    await serverFetch(`waypoints/${id}`, { method: 'GET' })
                        .then((data: IWaypoint) => {
                            return data;
                        })
                        .catch((ex: any) => {
                            exception(api, 'Ошибка получения маршрутной точки', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                if (id) {
                    setEntity(result[0][0]);
                } else {
                    setEntity({
                        id: undefined,
                        type: undefined,
                        name: undefined,
                        routeZone: routeZone,
                        allowAcceptance: false,
                        archived: false,
                        warehouseId: undefined,
                        headerId: headerId,
                    });
                }
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    const onFinish = () => {
        serverFetch(`waypoints`, { method: id ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                onSave();
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения маршрутной точки', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        entity && (
            <Modal
                destroyOnHidden={true}
                width={650}
                title={id ? 'Изменить маршрутную точку' : 'Добавить маршрутную точку'}
                open={true}
                okText='ОК'
                onOk={() => {
                    form.submit();
                }}
                onCancel={() => onCancel()}
            >
                <Form colon={false} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} onFinish={onFinish} form={form}>
                    <Form.Item
                        initialValue={entity.routeZone}
                        label='Зона маршрута'
                        name='routeZone'
                        required
                        rules={[{ required: true, message: 'Укажите зону маршрута' }]}
                    >
                        <Select
                            disabled={true}
                            onChange={(value) => {
                                setEntity({ ...entity, routeZone: value });
                            }}
                            options={routeZones.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.type}
                        label='Тип'
                        name='type'
                        required
                        rules={[{ required: true, message: 'Укажите тип точки' }]}
                    >
                        <Select
                            autoFocus
                            allowClear
                            disabled={isViewOnly}
                            onChange={(value) => {
                                setEntity({ ...entity, type: value });
                            }}
                            options={waypointTypes.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.sequence}
                        label='Порядковый номер'
                        name='sequence'
                        tooltip='Если поле оставить пустым, то запись добавится в конец списка автоматически'
                        rules={[
                            {
                                validator: async (_, value) => {
                                    const isSequenceExist =
                                        value &&
                                        (await serverFetch(`waypoints/${headerId}/${entity.id}/isexist/${value}`, {
                                            method: 'GET',
                                        })
                                            .then((data: any) => {
                                                return data;
                                            })
                                            .catch((ex: any) => {
                                                exception(api, 'Ошибка проверки порядкового номера', ex, () => d(userLoaded(undefined)));
                                            }));

                                    if (isSequenceExist) return Promise.reject('Запись с указанным порядковым номером уже существует');

                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <InputNumber
                            min={1}
                            disabled={isViewOnly}
                            onChange={(value: number | null) => {
                                setEntity({ ...entity, sequence: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item initialValue={entity.name} label='Название' name='name'>
                        <Input
                            disabled={isViewOnly}
                            onChange={(data) => {
                                setEntity({ ...entity, name: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label='Приемка груза' name='allowAcceptance' valuePropName='checked'>
                        <Switch
                            disabled={isViewOnly}
                            defaultChecked={entity?.allowAcceptance}
                            onChange={(value) => {
                                setEntity({ ...entity, allowAcceptance: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label='Опциональная' name='isOptional' valuePropName='checked'>
                        <Switch
                            disabled={isViewOnly}
                            defaultChecked={entity?.isOptional}
                            onChange={(value) => {
                                setEntity({ ...entity, isOptional: value });
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        )
    );
};

export default Waypoint;
